import { parseDate } from "../../Utils/dateSort";

// Function to get unique values from a column in the raw data
// const getUniqueValues = (rawData, columnField) => {
//   return Array.from(new Set(rawData.map((item) => item[columnField])));
// };

const multiLevelSort = (arr, config) => {
  // console.log(arr);
  return arr.sort((a, b) => {
    for (let { index, type, order, format } of config) {
      let valA = a[index];
      let valB = b[index];
      let comparison = 0;
      // console.log(index, type, order, format);

      // Handle null values: Treat null as the smallest value
      if (valA === null && valB === null) {
        continue; // Both values are null, move to the next sorting criterion
      }
      if (valA === null) return order === "asc" ? -1 : 1; // Null comes first/last based on order
      if (valB === null) return order === "asc" ? 1 : -1;

      // Determine comparison based on data type
      if (type === "String") {
        valA = String(valA); // Ensure valA is a string
        valB = String(valB); // Ensure valB is a string
        comparison = valA.localeCompare(valB);
      } else if (type === "Number") {
        valA = Number(valA); // Ensure valA is a number
        valB = Number(valB); // Ensure valB is a number
        comparison = valA - valB;
      } else if (type === "Date") {
        const dateA = parseDate(valA, format, 1);
        const dateB = parseDate(valB, format, 1);
        comparison = dateA - dateB;
      }

      // If values are different, return comparison (considering ascending/descending)
      if (comparison !== 0) {
        return order === "asc" ? comparison : -comparison;
      }
    }

    return 0; // If all comparisons are equal, arrays are considered equal
  });
};


const sortConcatenatedStrings = (uniqueColumnKeys, keyProperties) => {
  // assuming the values are concatenated in the same order of key properties
  const uniqueColumnKeysArray = uniqueColumnKeys.map((item) => {
    return item.split("|");
  });
  // console.log(JSON.stringify(uniqueColumnKeysArray));
  const sortConfig = keyProperties.map((item, index) => {
    const { dataType, format } = item;
    const sortObject = { index, type: dataType, format, order: "asc" };
    return sortObject;
  });

  const sortedColumnKeys = multiLevelSort(uniqueColumnKeysArray, sortConfig);
  // console.log(sortedColumnKeys);

  return sortedColumnKeys;
};

const getFlatKeys = (rawData, keys, keyProperties, getSorted = true) => {
  const combinedValues = rawData.map((row) =>
    keys.map((key) => (row[key] === null ? "null" : row[key])).join("|")
  );

  // console.log(combinedValues);
  const uniqueColumnKeys = Array.from(new Set(combinedValues));
  if (getSorted) {
    const sortedKeys = sortConcatenatedStrings(uniqueColumnKeys, keyProperties);
    // console.log(sortedKeys);
    const concatenatedSortedKeyArray = sortedKeys.map((item) => {
      const concatenatedString = item.join("|");
      return concatenatedString;
    });

    return concatenatedSortedKeyArray;
  } else {
    return uniqueColumnKeys;
  }
};

const attachMeasureKeys = (uniqueColumnValues, isMeasureOnTop, measureKeys) => {
  const noColumns = uniqueColumnValues[0] === "" && uniqueColumnValues.length === 1;
  if (noColumns) return measureKeys;
  return isMeasureOnTop
    ? measureKeys.flatMap((measureKey) =>
      uniqueColumnValues.map((colKey) => `${measureKey}|${colKey}`)
    )
    : uniqueColumnValues.flatMap((colKey) =>
      measureKeys.map((measureKey) => `${colKey}|${measureKey}`)
    );
};

const calculateColumnKey = (
  item,
  isMeasureOnTop,
  columnKeys,
  valueKeyArray
) => {
  const noColumns = columnKeys.length === 0;
  if (noColumns) return valueKeyArray;
  const flatColKey = columnKeys
    .map((colKey) => item[colKey] !== null && item[colKey] !== undefined ? item[colKey] : "null")
    .join("|");

  return isMeasureOnTop
    ? valueKeyArray.map((valKey) => `${valKey}|${flatColKey}`)
    : valueKeyArray.map((valKey) => `${flatColKey}|${valKey}`);
};

function getUniqueValuesAtIndex(index, array) {
  const frequencyMap = new Map();

  // Count occurrences of each value at the specified index
  array.forEach((subArray) => {
    const value = subArray[index];
    if (value !== undefined) {
      frequencyMap.set(value, (frequencyMap.get(value) || 0) + 1);
    }
  });

  // console.log("Frequency Map:", frequencyMap);

  // Convert the Map to an array of objects
  const result = Array.from(frequencyMap, ([key, count]) => ({
    value: key,
    frequency: count,
  }));

  // console.log("Result:", result);
  return result;
}


const getSubsequentLevelHeaderRow = (depthIndex, array, columnKeys) => {
  const numberColumns = array.length;
  const combinedValuesArray = [];
  // console.log(columnKeys, depthIndex);
  // console.log(array);
  const dataType = (depthIndex <= columnKeys.length - 1 && columnKeys[depthIndex]?.dataType)
    ? columnKeys[depthIndex].dataType
    : "Number";


  // console.log(dataType, depthIndex, columnKeys);



  array.forEach((item) => {
    const SlicedArrayString = item.slice(0, depthIndex + 1).join("|");
    combinedValuesArray.push(SlicedArrayString);
  });
  // console.log(combinedValuesArray);


  const uniqueValuesWithCountArray = Object.values(
    combinedValuesArray.reduce((acc, value) => {
      const destructuredArray = value.split("|");
      const caption = destructuredArray[depthIndex];
      if (!acc[value]) {
        acc[value] = { columnId: value, columnSpan: 0, rowSpan: 1, caption, dataType };
      }
      acc[value].columnSpan += 1;
      return acc;
    }, {})
  );
  return uniqueValuesWithCountArray;
};

const headerDestructure = (
  rowKeys,
  columnKeys,
  ValueKeys,
  uniqueColumnKeys,
  showRowTotals = false,
  showValueKeys = true
) => {
  // console.log(showRowTotals);

  if (columnKeys.length === 0) {
    //there is no column hence returning rows and values with row and rowSpan as 1
    const simpleHeader = [];

    rowKeys.forEach((item) => {
      const headerCellObject = {
        columnId: item.responseKey,
        caption: item.responseKey,
        columnSpan: 1,
        rowSpan: 1,
        dataType: item.dataType
      };
      simpleHeader.push(headerCellObject);
    });
    ValueKeys.forEach((item) => {
      // console.log(item);

      const headerCellObject = {
        columnId: item,
        caption: item,
        columnSpan: 1,
        rowSpan: 1,
        dataType: "Number"
      };
      simpleHeader.push(headerCellObject);
    });
    if (showRowTotals) {
      ValueKeys.forEach((item) => {
        const headerCellObject = {
          columnId: `Total ${item}`,
          caption: `Total ${item}`,
          columnSpan: 1,
          rowSpan: 1,
          dataType: "Number"
        };
        simpleHeader.push(headerCellObject);
      });
    }

    return [simpleHeader];
  } else {
    //this means there are columns and we need to build the header
    const headerArray = [];
    const depthOfHeader = showValueKeys
      ? columnKeys.length + 1
      : columnKeys.length;
    const uniqueColumnKeysArray = uniqueColumnKeys.map((item) => {
      return item.split("|");
    });
    // console.log(columnKeys);

    for (let i = 0; i < depthOfHeader; i++) {
      const headerRow = [];
      if (i === 0) {
        //add rows
        rowKeys.forEach((item) => {
          const headerCellObject = {
            columnId: item.responseKey,
            caption: item.responseKey,
            columnSpan: 1,
            rowSpan: depthOfHeader,
            dataType: item.dataType
          };
          headerRow.push(headerCellObject);
        });

        //add columns for columns
        const uniqueValuesWithFrequency = getUniqueValuesAtIndex(
          i,
          uniqueColumnKeysArray
        );
        uniqueValuesWithFrequency.forEach((item) => {
          const headerCellObject = {
            columnId: item.value,
            caption: item.value,
            columnSpan: item.frequency,
            rowSpan: 1,
            dataType: columnKeys[i].dataType
          };
          headerRow.push(headerCellObject);
        });
        if (showRowTotals) {
          // console.log("running");

          ValueKeys.forEach((item) => {
            const headerCellObject = {
              columnId: `Total ${item}`,
              caption: `Total ${item}`,
              columnSpan: 1,
              rowSpan: depthOfHeader,
              dataType: "Number"
            };
            headerRow.push(headerCellObject);
          });
        }
        headerArray.push(headerRow);
      } else {
        //this is not the first row, the rows and totals are already added, we dont need to add those now
        const subsequentHeaderRowArray = getSubsequentLevelHeaderRow(
          i,
          uniqueColumnKeysArray,
          columnKeys
        );

        headerArray.push(subsequentHeaderRowArray);
      }
    }

    return headerArray;
  }

  return null;
};

const createMaxMinObject = (firstRow, valueObjectArray) => {
  const minMaxObject = {};

  // Loop through the valueObjectArray to initialize min and max for each key
  valueObjectArray.forEach((item) => {
    // Initialize the object for the responseKey if it doesn't exist
    if (!minMaxObject[item.responseKey]) {
      minMaxObject[item.responseKey] = {};
    }

    // Set the min and max values to the value from firstRow
    minMaxObject[item.responseKey].min = Number(firstRow[item.responseKey]);
    minMaxObject[item.responseKey].max = Number(firstRow[item.responseKey]);
  });

  // console.log(minMaxObject);

  return minMaxObject;
};

const manageTotal = (
  measure,
  targetObject,
  targetKey,
  currentCellValue,
  rowTotalRequired,
  keyedRowTotals,
  calculatedRowKey
) => {
  switch (measure) {
    case "calculatedField":
    case "COUNT":
    case "SUM":
      targetObject[targetKey] += currentCellValue;
      break;
    case "MIN":
      if (!targetObject[targetKey]) {
        return (targetObject[targetKey] = currentCellValue);
      }
      if (currentCellValue < targetObject[targetKey]) {
        targetObject[targetKey] = currentCellValue;
      }

      break;
    case "MAX":
      if (!targetObject[targetKey]) {
        return (targetObject[targetKey] = currentCellValue);
      }
      if (currentCellValue > targetObject[targetKey]) {
        targetObject[targetKey] = currentCellValue;
      }
      break;
    case "DISTINCT":
    case "AVG":
      if (rowTotalRequired) {
        const trimmedStr = targetKey.slice(6);
        const requiredValue = keyedRowTotals[calculatedRowKey]?.[trimmedStr];
        targetObject[targetKey] = Number(requiredValue);
      }

      break;

    default:
      targetObject[targetKey] += currentCellValue;
      break;
  }
};
// Function to transform data for a pivot table
const pivotiseData = ({
  rawData,
  rawGroupByKeys,
  rawRowKeys,
  valueObjectArray,
  isRowTotalVisible,
  isColumnTotalVisible,
  isMeasureOnTop,
  totalData,
  grandTotalRequired,
  columnTotalRequired,
  rowTotalRequired,
  quickTableArray,
}) => {
  // console.log(valueObjectArray);

  const rowKeys = rawRowKeys.map((item) => item.responseKey);
  const columnKeys = rawGroupByKeys.map((item) => item.responseKey);
  // // console.log(columnKeys);
  const valueKeyArray = valueObjectArray.map((item) => item.responseKey);
  const maxMinData = createMaxMinObject(rawData[0], valueObjectArray);
  const valueKeyMeasures = valueObjectArray.map((item) => item.value);

  const { rowTotals, columnTotals, grandTotals } = totalData;
  // console.log(valueKeyArray);
  // // console.log(grandTotalRequired, columnTotalRequired, rowTotalRequired);

  let keyedRowTotals = {};
  let keyedColunTotals = {};
  let keyedGrandTotals = {};

  if (rowTotalRequired) {
    keyedRowTotals = {};
    rowTotals !== null &&
      rowTotals.forEach((item) => {
        const formulatedKey = rowKeys.map((rowKey) => item[rowKey]).join("|");

        keyedRowTotals[formulatedKey] = item;
      });
  }

  if (grandTotalRequired) {
    const grandTotalObjectKeys = Object.keys(
      grandTotals !== null && grandTotals[0]
    );
    grandTotalObjectKeys.forEach((key) => {
      const formulatedKey = `Total ${key}`;
      keyedGrandTotals[formulatedKey] =
        grandTotals !== null && grandTotals[0][key];
    });
  }

  const uniqueColumnValues = getFlatKeys(
    rawData,
    columnKeys,
    rawGroupByKeys,
    true
  );
  // console.log(uniqueColumnValues);
  const measureAttachedColumnValueKeys = attachMeasureKeys(
    uniqueColumnValues,
    isMeasureOnTop,
    valueKeyArray
  );

  const flatUniqueRowsArray = getFlatKeys(rawData, rowKeys, rawRowKeys, false);
  const tableMap = new Map();

  flatUniqueRowsArray.forEach((flatRow) => {
    const destructuredRowValueArray = flatRow.split("|");
    const rowObject = { rowKey: flatRow };

    rowKeys.forEach((rowKey, index) => {
      rowObject[rowKey] = destructuredRowValueArray[index];
    });

    measureAttachedColumnValueKeys.forEach((colKey) => {
      rowObject[colKey] = 0;
    });
    valueKeyArray.forEach((valKey) => {
      const totalKey = `Total ${valKey}`;
      rowObject[totalKey] = null;
    });

    tableMap.set(flatRow, rowObject);
  });



  const columnTotalObject = {};

  measureAttachedColumnValueKeys.forEach((measureAttachedColumnKey) => {
    columnTotalObject[measureAttachedColumnKey] = 0;
  });

  valueKeyArray.forEach((valKey) => {
    const totalKey = `Total ${valKey}`;
    columnTotalObject[totalKey] = null; // Initialize row totals
  });

  rawData.forEach((item) => {
    const calculatedRowKey = rowKeys
      .map((rowKey) => (item[rowKey] !== null && item[rowKey] !== undefined ? item[rowKey] : "null"))
      .join("|");

    // console.log(rowKeys);
    const rowObject = tableMap.get(calculatedRowKey);

    if (rowObject) {
      const calculatedColumnKeys = calculateColumnKey(
        item,
        isMeasureOnTop,
        columnKeys,
        valueKeyArray
      );
      // console.log(calculatedColumnKeys);

      calculatedColumnKeys.forEach((colKey, index) => {
        const valueKey = valueKeyArray[index];
        if (item[valueKey] !== undefined) {
          rowObject[colKey] = Number(item[valueKey]);
          //update column total column
          manageTotal(
            valueKeyMeasures[index],
            columnTotalObject,
            colKey,
            Number(item[valueKey])
          );
          // manageTotal(valueKeyMeasures[index],columnTotalObject[colKey],Number(item[valueKey]))
        }
      });

      valueKeyArray.forEach((valKey, index) => {
        // console.log(valKey);
        const quickObject = quickTableArray.find(
          (quick) => quick.quickTableKey === valKey
        );
        // console.log(quickObject !== undefined && quickObject.quickOption);

        const totalKey = `Total ${valKey}`;

        const cellValue = Number(item[valKey]);
        //update the totals

        //update rowTotal
        manageTotal(
          valueKeyMeasures[index],
          rowObject,
          totalKey,
          cellValue,
          rowTotalRequired,
          keyedRowTotals,
          calculatedRowKey
        );
        // update grand Total
        manageTotal(
          valueKeyMeasures[index],
          columnTotalObject,
          totalKey,
          cellValue
        );

        // Update the minMax directly in the maxMinData object
        if (cellValue > maxMinData[valKey].max) {
          maxMinData[valKey].max = cellValue;
        }
        if (cellValue < maxMinData[valKey].min) {
          maxMinData[valKey].min = cellValue;
        }
      });
    } else {
      console.warn(
        `There is an unutilized row in the raw data: ${JSON.stringify(item)}`
      );
    }
  });

  // console.log(columnTotalObject);
  const totalRowForColumnTotals = {};

  // Create the total row
  rowKeys.forEach((key) => {
    totalRowForColumnTotals[key] = "Grand Total";
  });

  measureAttachedColumnValueKeys.forEach((key) => {
    totalRowForColumnTotals[key] = columnTotalObject[key];
  });

  valueKeyArray.forEach((valKey) => {
    const totalKey = `Total ${valKey}`;
    totalRowForColumnTotals[totalKey] = columnTotalObject[totalKey];
  });

  if (grandTotalRequired) {
    const importedGrandTotalKeys = Object.keys(
      grandTotals !== null && grandTotals[0]
    );
    importedGrandTotalKeys.forEach((importedTotalKey) => {
      const formulatedKey = `Total ${importedTotalKey}`;
      totalRowForColumnTotals[formulatedKey] = Number(grandTotals[0][importedTotalKey]);
    });
  }

  if (columnTotalRequired) {
    const allObjectKeysinColumnTotal = Object.keys(columnTotals[0]);
    const remainingValueKeysinColumnTotal = allObjectKeysinColumnTotal.filter(
      (value) => !columnKeys.includes(value)
    );

    columnTotals.forEach((item) => {
      const calculatedColumnKeys = calculateColumnKey(
        item,
        isMeasureOnTop,
        columnKeys,
        remainingValueKeysinColumnTotal
      );

      calculatedColumnKeys.forEach((key, index) => {
        totalRowForColumnTotals[key] =
          Number(item[remainingValueKeysinColumnTotal[index]]);
      });
    });
  }

  // Convert the map to an array and add the total row to uniqueTableRows
  const uniqueTableRows = Array.from(tableMap.values());

  const destructuredHeader = headerDestructure(
    rawRowKeys,
    rawGroupByKeys,
    valueKeyArray,
    measureAttachedColumnValueKeys,
    isRowTotalVisible
  );

  const headerKeySequence = [...rowKeys, ...measureAttachedColumnValueKeys];
  const rowTotalColumnKeys = [];
  if (isRowTotalVisible) {
    valueKeyArray.forEach((item) => {
      headerKeySequence.push(`Total ${item}`);
      rowTotalColumnKeys.push(`Total ${item}`);
    });
  }

  // console.log(uniqueTableRows)

  return {
    uniqueTableRows,
    measureAttachedColumnValueKeys,
    totalRowForColumnTotals,
    destructuredHeader,
    headerKeySequence,
    rowTotalColumnKeys,
    maxMinData,
  };
};

export { pivotiseData, createMaxMinObject, getFlatKeys };